import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus, Edit2, Trash2, Download, Printer, FileText } from 'lucide-react';
import { DataStore } from '@/lib/data-store';
import { DataForm } from '@/components/DataForm';
import { ProjectBreadcrumb } from '@/components/ProjectBreadcrumb';
import { useProject } from '@/context/ProjectContext';
import { exportToExcel, printTable } from '@/lib/export-utils';
import { printWithLetterhead, generateTableHTML } from '@/lib/print-utils';
import { generateInvoiceNumber, getNextInvoiceNumber, calculateInvoiceStatus, getSisaBayar } from '@/lib/invoice-utils';

const Invoice = () => {
  const [invoices, setInvoices] = useState(DataStore.getData().invoices);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { selectedProject, setSelectedProject } = useProject();

  const handleAddNew = () => {
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setShowForm(true);
  };

  const handleSubmit = (data: any) => {
    const newInvoices = editingId
      ? invoices.map(i => i.id === editingId ? { ...data, id: editingId } : i)
      : [...invoices, { ...data, id: Date.now().toString() }];
    
    setInvoices(newInvoices);
    DataStore.saveData({ ...DataStore.getData(), invoices: newInvoices });
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    const filtered = invoices.filter(i => i.id !== id);
    setInvoices(filtered);
    DataStore.saveData({ ...DataStore.getData(), invoices: filtered });
  };

  const filteredInvoices = useMemo(() => invoices.filter(i => {
    const matchesSearch = i.no.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          i.pelanggan.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProject = !selectedProject || i.projek === selectedProject;
    return matchesSearch && matchesProject;
  }), [invoices, searchTerm, selectedProject]);

  const nextInvoiceNo = useMemo(() => generateInvoiceNumber(getNextInvoiceNumber(invoices)), [invoices]);

  const formFields = [
    { name: 'pelanggan', label: 'Pelanggan', type: 'text', required: true },
    { name: 'tanggal', label: 'Tanggal', type: 'date', required: true },
    { name: 'total', label: 'Total (RM)', type: 'number', required: true },
    { name: 'bayar', label: 'Pembayaran (RM)', type: 'number', required: true },
  ];

  return (
    <div className="space-y-6">
      {selectedProject && (
        <ProjectBreadcrumb projectName={selectedProject} currentPage="invoice" />
      )}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Invoice</h1>
          <p className="text-slate-600 mt-1">Kelola invoice dan pembayaran</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {invoices.length > 0 && (
            <>
              <Button 
                onClick={() => {
                  const tableHTML = generateTableHTML(filteredInvoices, [
                    { label: 'No. Invoice', key: 'no' },
                    { label: 'Pelanggan', key: 'pelanggan' },
                    { label: 'Tanggal', key: 'tanggal' },
                    { label: 'Total (RM)', key: 'total', format: (v) => `RM ${v.toFixed(2)}` },
                    { label: 'Pembayaran (RM)', key: 'bayar', format: (v) => `RM ${v.toFixed(2)}` },
                    { label: 'Status', key: 'status' }
                  ]);
                  printWithLetterhead('DAFTAR INVOICE', tableHTML, 'INVOIS / INVOICE');
                }} 
                className="bg-green-600 hover:bg-green-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Printer size={18} />
                <span className="font-semibold">Cetak</span>
              </Button>
              <Button 
                onClick={() => exportToExcel(filteredInvoices, 'Invoice')} 
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Download size={18} />
                <span className="font-semibold">Excel</span>
              </Button>
            </>
          )}
          <Button className="bg-red-600 hover:bg-red-700 gap-2 px-4 py-2 h-auto" onClick={handleAddNew}>
            <Plus size={18} />
            <span className="font-semibold">Invoice Baru</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-slate-400" size={20} />
            <Input 
              placeholder="Cari invoice..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-slate-300"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Daftar Invoice</CardTitle>
          <CardDescription>Total {filteredInvoices.length} invoice</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Tidak ada data invoice. Silakan tambahkan invoice baru.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table id="invoices-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">No. Invoice</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Pelanggan</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Tanggal</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Total</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Pembayaran</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice: any) => {
                    const sisa = getSisaBayar(invoice.total, invoice.bayar);
                    return (
                    <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedProject(invoice.pelanggan)}>
                      <td className="py-3 px-4 font-medium text-slate-900">{invoice.no}</td>
                      <td className="py-3 px-4 text-slate-600 font-medium text-blue-600 hover:underline">{invoice.pelanggan}</td>
                      <td className="py-3 px-4 text-slate-600">{invoice.tanggal}</td>
                      <td className="py-3 px-4 font-medium text-slate-900 text-right">RM {invoice.total.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-medium">RM {invoice.bayar.toFixed(2)}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                          invoice.status === 'Lunas' ? 'bg-green-100 text-green-700' :
                          invoice.status === 'Sebagian' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 flex gap-2">
                        <button onClick={() => handleEdit(invoice.id)} className="text-blue-600 hover:text-blue-700 text-sm gap-1 flex items-center">
                          <Edit2 size={14} /> Edit
                        </button>
                        <button onClick={() => handleDelete(invoice.id)} className="text-red-600 hover:text-red-700 text-sm gap-1 flex items-center">
                          <Trash2 size={14} /> Hapus
                        </button>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {showForm && (
        <DataForm
          title={editingId ? 'Edit Invoice' : 'Tambah Invoice Baru'}
          fields={formFields}
          initialData={editingId ? invoices.find(i => i.id === editingId) : { no: nextInvoiceNo, status: 'Belum' }}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
};

export default Invoice;